import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { TwoFactorModal } from '@/components/TwoFactorModal';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [pendingLogin, setPendingLogin] = useState<{ email: string; password: string } | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validación básica
    if (!formData.email || !formData.password) {
      setError('Por favor completa todos los campos');
      return;
    }

    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast({
        title: "¡Bienvenido!",
        description: "Has iniciado sesión exitosamente",
      });
      navigate('/');
    } catch (err: unknown) {
      console.error('Login error:', err);
      
      // Si se requiere 2FA, mostrar modal de 2FA
      if (err && typeof err === 'object' && 'response' in err && 
          err.response && typeof err.response === 'object' && 'status' in err.response && 
          err.response.status === 422) {
        setPendingLogin({ email: formData.email, password: formData.password });
        setShowTwoFactor(true);
        setLoading(false);
        return;
      }
      
      let errorMessage = 'Error al iniciar sesión';
      
      if (err && typeof err === 'object') {
        if ('response' in err && err.response && typeof err.response === 'object' && 
            'data' in err.response && err.response.data && typeof err.response.data === 'object' && 
            'message' in err.response.data) {
          errorMessage = err.response.data.message as string;
        } else if ('message' in err && typeof err.message === 'string') {
          errorMessage = err.message;
        } else if ('code' in err && err.code === 'NETWORK_ERROR') {
          errorMessage = 'Error de conexión. Verifica que el servidor esté funcionando.';
        } else if (!navigator.onLine) {
          errorMessage = 'Sin conexión a internet. Verifica tu conexión.';
        }
      }
      
      setError(errorMessage);
      toast({
        title: "Error al iniciar sesión",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorVerify = async (code: string) => {
    if (!pendingLogin) return;

    try {
      await login(pendingLogin.email, pendingLogin.password, code);
      
      toast({
        title: "¡Bienvenido!",
        description: "Has iniciado sesión exitosamente",
      });
      
      setShowTwoFactor(false);
      setPendingLogin(null);
      navigate('/');
    } catch (err: unknown) {
      let errorMessage = 'Código de verificación inválido';
      if (err && typeof err === 'object' && 'response' in err && 
          err.response && typeof err.response === 'object' && 'data' in err.response &&
          err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data) {
        errorMessage = err.response.data.message as string;
      }
      throw new Error(errorMessage);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-peach/30 to-primary/20 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src="/logo.png" 
              alt="Vivi con Amor y Arte" 
              className="h-20 w-auto mx-auto mb-4"
            />
          </Link>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-playfair text-center">Iniciar Sesión</CardTitle>
            <CardDescription className="text-center">
              Ingresa tus credenciales para acceder a tu cuenta
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary-dark"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">¿No tienes una cuenta? </span>
                <Link 
                  to="/register" 
                  className="text-primary hover:underline font-medium"
                >
                  Regístrate aquí
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>

      </div>
      </div>

      <TwoFactorModal
        isOpen={showTwoFactor}
        onClose={() => {
          setShowTwoFactor(false);
          setPendingLogin(null);
        }}
        onVerify={handleTwoFactorVerify}
        loading={loading}
        error={error}
      />
    </>
  );
};

export default Login; 